import React, { useState, useEffect } from 'react';
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Box, IconButton, MenuItem, Select } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import './MyPicksNavBar.scss';

/**
 * A simple navigation bar for date/interval selection
 */
const MyPicksDateNavBar = ({ theCurrentInterval, intervalOptions }) => {
    const {
        intervalInfo,
        myPicksNavBarInfo,
        gameId,
        masterPoolId,
        page,
        gameAddress,
        pickIndex,
        unsavedPicks,
        setUnsavedPicksModalOpen,
        setUrlForNav,
    } = useUserAndGameContext();
    //console.log("The Current Interval:", theCurrentInterval);
    
    const navigate = useNavigate();
    const [selectedIndex, setSelectedIndex] = useState(0);
    
    // Format dates properly for display
    const formattedOptions = intervalOptions?.map(date => ({
        value: date,
        label: formatDateForDisplay(date)
    })) || [];
    
    // On mount, find the current interval in options
    useEffect(() => {
        if (intervalOptions?.length && theCurrentInterval) {
            const index = intervalOptions.findIndex(date => date === theCurrentInterval);
            if (index >= 0) {
                setSelectedIndex(index);
            }
        }
    }, [intervalOptions, theCurrentInterval]);
    
    // Helper function to format date for display
    function formatDateForDisplay(dateString) {
        if (!dateString) return '';
        try {
            const date = DateTime.fromISO(dateString);
            return date.toFormat('EEE MMM d'); // Format as "Wed Apr 16"
        } catch (error) {
            console.error("Error formatting date:", error);
            return dateString;
        }
    }
    
    // Handle navigation to new interval
    function navigateToInterval(intervalValue) {
        // Build URL with all current params plus the new interval
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('interval', intervalValue);
        
        // Other standard params
        if (masterPoolId) searchParams.set('mId', masterPoolId);
        if (gameId) searchParams.set('gId', gameId);
        if (page) searchParams.set('page', page);
        if (pickIndex) searchParams.set('pickIndex', pickIndex);
        
        // Create the full URL
        const encodedAddress = encodeURIComponent(gameAddress);
        const url = `/${encodedAddress}?${searchParams.toString()}`;
        
        // Navigate directly
        navigate(url);
    }
    
    // Handle arrow navigation
    function handleArrowClick(direction) {
        if (!intervalOptions?.length) return;
        
        let newIndex;
        if (direction === 'next') {
            newIndex = Math.min(selectedIndex + 1, intervalOptions.length - 1);
        } else {
            newIndex = Math.max(selectedIndex - 1, 0);
        }
        
        const newInterval = intervalOptions[newIndex];
        //console.log("New Interval:", newInterval);
        
        if (unsavedPicks) {
            // Handle unsaved picks case
            setUnsavedPicksModalOpen(true);
            setUrlForNav(`/${encodeURIComponent(gameAddress)}?mId=${masterPoolId}&gId=${gameId}&page=${page}&interval=${newInterval}&pickIndex=${pickIndex}`);
            return;
        }
        
        // Update the index and navigate
        setSelectedIndex(newIndex);
        navigateToInterval(newInterval);
    }
    
    // Handle dropdown selection
    function handleSelectChange(event) {
        const newValue = event.target.value;
        const newIndex = intervalOptions.findIndex(date => date === newValue);
        
        if (unsavedPicks) {
            // Handle unsaved picks case
            setUnsavedPicksModalOpen(true);
            setUrlForNav(`/${encodeURIComponent(gameAddress)}?mId=${masterPoolId}&gId=${gameId}&page=${page}&interval=${newValue}&pickIndex=${pickIndex}`);
            return;
        }
        
        // Update the index and navigate
        if (newIndex >= 0) {
            setSelectedIndex(newIndex);
        }
        navigateToInterval(newValue);
    }
    
    // Don't render if no options
    if (!intervalOptions?.length) return null;
    
    // Get current value for display
    const currentValue = intervalOptions[selectedIndex] || '';
    
    return (
        <Box 
            className="my-picks-nav-bar"
            sx={{ 
                backgroundColor: myPicksNavBarInfo?.backgroundColor || '#07356B',
                color: myPicksNavBarInfo?.textColor || '#FFC60A',
            }}
        >
            <Box className="my-picks-nav-bar-container">
                <Box className="my-picks-nav-bar-content">
                    <IconButton 
                        onClick={() => handleArrowClick('prev')}
                        disabled={selectedIndex === 0}
                    >
                        <ArrowBackIosNewIcon
                            className="my-picks-nav-bar-arrow-icon" 
                            sx={{ color: myPicksNavBarInfo?.textColor || '#FFC60A' }}
                        />
                    </IconButton>
                    <Box className="my-picks-nav-bar-select-container">
                        <Select
                            value={currentValue}
                            onChange={handleSelectChange}
                            variant="outlined"
                            className="my-picks-nav-bar-select"
                            sx={{ 
                                color: myPicksNavBarInfo?.textColor || '#FFC60A',
                                '& .MuiSvgIcon-root': {
                                    color: myPicksNavBarInfo?.textColor || '#FFC60A'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent'
                                }
                            }}
                            renderValue={(value) => formatDateForDisplay(value)}
                        >
                            {formattedOptions.map((option) => (
                                <MenuItem 
                                    key={option.value} 
                                    value={option.value}
                                    sx={{ color: '#000000' }}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <IconButton 
                        onClick={() => handleArrowClick('next')}
                        disabled={selectedIndex >= intervalOptions.length - 1}
                    >
                        <ArrowBackIosNewIcon 
                            className="my-picks-nav-bar-arrow-icon"
                            sx={{ color: myPicksNavBarInfo?.textColor || '#FFC60A', transform: 'rotate(180deg)' }}
                        />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default MyPicksDateNavBar;